import React from "react";

import Centile from "pages/customer/tools/location/filters/targets/Centile";
import CentileMetric from "pages/customer/tools/location/filters/targets/CentileMetric";

import {
    selectCandidateComparatorStores,
    selectCandidateTarget,
    selectAggregatedDirectCompetitors,
    selectRetailCentresCentiles,
    selectStoreCategoryCentilesMetrics,
    setCandidateTarget
} from "modules/customer/tools/location/filters/filtersSlice";
import { useAppDispatch, useAppSelector } from "store";
import numberFormatter from "utils/numberFormatter";

const Urbanicity: React.FC = () => {
    const dispatch = useAppDispatch();
    const candidateComparatorStores = useAppSelector(selectCandidateComparatorStores);
    const retailCentresCentiles = useAppSelector(selectRetailCentresCentiles);
    const comparatorStoresCentiles = candidateComparatorStores.map(store => {
        const retailCentreCentile = retailCentresCentiles.find(rcc => rcc.retailCentreId === store.retailCentre.id)?.urbanicity ?? 0;
        return {
            storeName: store.name,
            centileValue: retailCentreCentile
        };
    });
    const aggregatedDirectCompetitors = useAppSelector(selectAggregatedDirectCompetitors);
    const aggregatedDirectCompetitorCentiles = aggregatedDirectCompetitors.map(item => ({
        storeName: item.fascia,
        centileValue: item.urbanicityCentile
    }));
    const candidateTarget = useAppSelector(selectCandidateTarget);
    const storeCategoryCentilesMetrics = useAppSelector(selectStoreCategoryCentilesMetrics);
    const centileMetric = storeCategoryCentilesMetrics.find(cm => cm.centile === candidateTarget?.urbanicity);
    const name = "Urbanicity";
    const description = `The urbanicity score looks at the catchment area and evaluates the population density,
        residents' access to cars, and the percentage of residents who live in flats.`;
    const urbanicityCentileMetrics = [{
        name: "Population density",
        tooltip: "The median population density bucket in the catchment area.",
        value: centileMetric?.populationDensityBracket ?? ""
    }, {
        name: "Living in flats",
        tooltip: "The percentage of the catchment area's population that live in flats.",
        value: numberFormatter.toPercentage(centileMetric?.livingInFlatPercentage ?? 0, false, 0)
    }, {
        name: "Number of cars",
        tooltip: "The median number of cars per household in the catchment area.",
        value: numberFormatter.toDecimalPlaces(centileMetric?.numberOfCars ?? 0, 0)
    }];

    const handleSliderChange = (value: number) => {
        if (candidateTarget) {
            const newCandidateTarget = {
                ...candidateTarget,
                urbanicity: value
            };
            dispatch(setCandidateTarget(newCandidateTarget));
        }
    };

    if (!candidateTarget) {
        return null;
    }

    return (
        <Centile
            name={name}
            description={description}
            disabled={!candidateTarget.useDemographics}
            sliderValue={candidateTarget.urbanicity}
            onSliderChange={handleSliderChange}
            comparatorStoresCentiles={comparatorStoresCentiles}
            aggregatedDirectCompetitorCentiles={aggregatedDirectCompetitorCentiles}
            chartLowLabel="Very rural area"
            chartHighLabel="Very urban area"
        >
            {urbanicityCentileMetrics.map((metric, index) =>
                <CentileMetric
                    key={index}
                    name={metric.name}
                    tooltip={metric.tooltip}
                    value={metric.value}
                    disabled={!candidateTarget.useDemographics}
                />
            )}
        </Centile>
    );
};

export default Urbanicity;
